<template>
  <div class="wizard-overlay">
    <div class="wizard-container">
      
      <!-- Step 1: Introduction -->
      <div v-if="step === 1" class="wizard-step step-intro">
      
        <div class="step-content-scroll">
          <div class="intro-header">
          
            <h2 class="hero-title">{{ $t('welcomeWizard.heroPrefix') }} <span class="gradient-text">{{ $t('welcomeWizard.heroHighlight') }}</span></h2>
            <p class="hero-subtitle">{{ $t('welcomeWizard.heroSubtitle') }}</p>
          </div>
          
          <div class="info-card">
            <p class="info-text">
              {{ $t('welcomeWizard.introText') }}
            </p>
            <a href="https://github.com/Auj625197595/rainsee-ai" target="_blank" class="github-repo-link">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
              </svg>
              <span>{{ $t('welcomeWizard.openSource', { repo: 'Auj625197595/rainsee-ai' }) }}</span>
            </a>
          </div>
          
          <div class="feature-grid">
            <div class="feature-card">
              <div class="card-icon-wrapper memory-bg">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"></path>
                  <path d="M8.5 8.5v.01"></path>
                  <path d="M16 12v.01"></path>
                  <path d="M12 16v.01"></path>
                </svg>
              </div>
              <div class="card-content">
                <h4>{{ $t('welcomeWizard.memoryTitle') }}</h4>
                <p>{{ $t('welcomeWizard.memoryDesc') }}</p>
              </div>
            </div>
            
            <div class="feature-card">
              <div class="card-icon-wrapper privacy-bg">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
              </div>
              <div class="card-content">
                <h4>{{ $t('welcomeWizard.privacyTitle') }}</h4>
                <p>{{ $t('welcomeWizard.privacyDesc') }}</p>
              </div>
            </div>
          </div>

          <div class="capability-badges">
            <div class="badge">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                <path d="M2 2l7.586 7.586"></path>
                <circle cx="11" cy="11" r="2"></circle>
              </svg>
              <span>{{ $t('welcomeWizard.imageSupport') }}</span>
            </div>
            <div class="badge">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <span>{{ $t('welcomeWizard.planMode') }}</span>
            </div>
            <div class="badge">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              <span>{{ $t('welcomeWizard.unlimitedChat') }}</span>
            </div>
          </div>
        </div>

        <div class="step-footer-fixed">
          <button class="start-btn" @click="nextStep">
            <span>{{ $t('welcomeWizard.startConfig') }}</span>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </button>
        </div>
      </div>

      <!-- Step 2: Configure Interface -->
      <div v-else-if="step === 2" class="wizard-step step-config">
        <div class="step-content-scroll">
          <div class="step-header">
            <h3>{{ $t('welcomeWizard.configTitle') }}</h3>
            <p>{{ $t('welcomeWizard.configDesc') }}</p>
          </div>

          <div class="config-form">
            <div class="form-group">
              <div class="provider-label-row">
                <label>{{ $t('welcomeWizard.provider') }}</label>
                <a v-if="currentProvider && currentProvider.course" 
                   :href="currentProvider.course" 
                   target="_blank" 
                   class="tutorial-link"
                   :title="$t('welcomeWizard.viewTutorialTitle')">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  <span>{{ $t('welcomeWizard.viewTutorial') }}</span>
                </a>
              </div>
              <select v-model="modelForm.provider" @change="handleProviderChange">
                <option v-for="p in providers" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
            </div>

            <div class="form-group">
              <label>{{ $t('welcomeWizard.configName') }}</label>
              <input type="text" v-model="modelForm.name" :placeholder="$t('welcomeWizard.configNamePlaceholder')">
            </div>

            <div class="form-group">
              <label>{{ $t('welcomeWizard.endpoint') }}</label>
              <input type="text" v-model="modelForm.endpoint" placeholder="https://host/v1/chat/completions">
              <small class="hint">{{ $t('welcomeWizard.endpointHint') }}</small>
            </div>

            <div class="form-group">
              <label>{{ $t('welcomeWizard.apiKey') }}</label>
              <input  v-model="modelForm.apiKey" placeholder="sk-...">
            </div>

            <div class="form-group">
              <div class="field-label-row">
                <label>{{ $t('welcomeWizard.modelName') }}</label>
                <span
                  class="inline-fetch-action"
                  :class="{ disabled: modelListLoading }"
                  role="button"
                  tabindex="0"
                  @click="fetchModelList"
                  @keydown.enter.prevent="fetchModelList"
                  @keydown.space.prevent="fetchModelList"
                >
                  {{ modelListLoading ? $t('settingsModal.fetchingModels') : $t('settingsModal.fetchModels') }}
                </span>
              </div>
              <div ref="modelCombobox" class="custom-model-combobox">
                <div
                  class="custom-combobox-input"
                  :class="{ open: modelDropdownOpen, disabled: modelListLoading }"
                >
                  <input
                    ref="modelInput"
                    type="text"
                    v-model="modelForm.model"
                    placeholder="..."
                    class="custom-combobox-field"
                    @focus="handleModelInputFocus"
                    @input="handleModelInputChange"
                    @keydown.down.prevent="moveModelDropdown(1)"
                    @keydown.up.prevent="moveModelDropdown(-1)"
                    @keydown.enter.prevent="confirmHighlightedModel"
                    @keydown.esc.prevent="closeModelDropdown"
                  >
                  <span
                    class="custom-combobox-toggle"
                    :class="{ disabled: !availableModels.length }"
                    @click.stop="toggleModelDropdown"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </span>
                </div>
                <div v-if="modelDropdownOpen" class="custom-combobox-panel">
                  <div
                    v-for="(item, index) in filteredAvailableModels"
                    :key="item.id"
                    class="custom-combobox-option"
                    :class="{
                      active: item.id === selectedFetchedModel,
                      highlighted: index === highlightedModelIndex
                    }"
                    @mousedown.prevent="selectFetchedModel(item)"
                  >
                    <div class="custom-combobox-option-title">{{ item.id }}</div>
                    <div v-if="item.displayName || item.ownedBy" class="custom-combobox-option-meta">
                      {{ item.displayName || item.ownedBy }}
                    </div>
                  </div>
                  <div v-if="!filteredAvailableModels.length" class="custom-combobox-empty">
                    {{ $t('settingsModal.noMatchingModels') }}
                  </div>
                </div>
              </div>
              <div v-if="modelListError" class="model-fetch-feedback error">{{ modelListError }}</div>
              <div v-else-if="modelListHint" class="model-fetch-feedback">
                {{ modelListHint }}
                <span v-if="lastModelCatalogSource"> ({{ lastModelCatalogSource }})</span>
              </div>
            </div>
          </div>
        </div>

        <div class="step-actions step-footer-fixed">
          <button class="btn-secondary" @click="step = 1">{{ $t('welcomeWizard.previous') }}</button>
          <button class="cool-btn" @click="saveAndStart" :disabled="!isValid">
            <span>{{ $t('welcomeWizard.startUsing') }}</span>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { store, mutations } from '../store';
import providersData from '../config/providers.json';
import { requestModelCatalog, validateModelCatalogEndpoint, MODEL_CATALOG_VALIDATION_ERRORS } from '../utils/modelCatalog';

export default {
  name: 'WelcomeWizard',
  props: {
    initialStep: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      step: this.initialStep,
      providers: providersData,
      modelListLoading: false,
      modelListError: '',
      modelListHint: '',
      availableModels: [],
      modelDropdownOpen: false,
      highlightedModelIndex: -1,
      selectedFetchedModel: '',
      lastModelCatalogSource: '',
      modelForm: {
        provider: '',
        name: '',
        endpoint: '',
        apiKey: '',
        model: '',
        type: 'text' // Fixed as text generation for the wizard
      }
    };
  },
  created() {
    this.modelForm.name = this.$t('welcomeWizard.defaultModelName');
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick);
  },
  computed: {
    isValid() {
      return this.modelForm.name && 
             this.modelForm.endpoint && 
             this.modelForm.apiKey && 
             this.modelForm.model;
    },
    currentProvider() {
      return this.providers.find(p => p.id === this.modelForm.provider);
    },
    filteredAvailableModels() {
      const models = Array.isArray(this.availableModels) ? this.availableModels : [];
      const query = (this.modelForm.model || '').trim().toLowerCase();
      if (!query) return models;

      return models.filter(item => {
        const candidates = [
          item.id,
          item.label,
          item.displayName,
          item.ownedBy
        ];

        return candidates.some(value => String(value || '').toLowerCase().includes(query));
      });
    }
  },
  methods: {
    resetFetchedModelOptions() {
      this.modelListLoading = false;
      this.modelListError = '';
      this.modelListHint = '';
      this.availableModels = [];
      this.modelDropdownOpen = false;
      this.highlightedModelIndex = -1;
      this.selectedFetchedModel = '';
      this.lastModelCatalogSource = '';
    },
    getModelListValidationMessage(code) {
      if (code === MODEL_CATALOG_VALIDATION_ERRORS.NEED_ENDPOINT) {
        return this.$t('settingsModal.fetchModelsNeedEndpoint');
      }
      if (code === MODEL_CATALOG_VALIDATION_ERRORS.INVALID_ENDPOINT) {
        return this.$t('settingsModal.fetchModelsInvalidEndpoint');
      }
      if (code === MODEL_CATALOG_VALIDATION_ERRORS.NEED_REAL_HOST) {
        return this.$t('settingsModal.fetchModelsNeedRealHost');
      }
      return this.$t('settingsModal.fetchModelsFailed');
    },
    openModelDropdown() {
      if (!this.availableModels.length) return;
      this.modelDropdownOpen = true;
      const selectedIndex = this.filteredAvailableModels.findIndex(item => item.id === this.selectedFetchedModel);
      this.highlightedModelIndex = selectedIndex >= 0 ? selectedIndex : (this.filteredAvailableModels.length ? 0 : -1);
    },
    closeModelDropdown() {
      this.modelDropdownOpen = false;
      this.highlightedModelIndex = -1;
    },
    toggleModelDropdown() {
      if (!this.availableModels.length) return;
      if (this.modelDropdownOpen) {
        this.closeModelDropdown();
        return;
      }

      this.openModelDropdown();
      this.$nextTick(() => {
        if (this.$refs.modelInput) {
          this.$refs.modelInput.focus();
        }
      });
    },
    handleModelInputFocus() {
      if (this.availableModels.length) {
        this.openModelDropdown();
      }
    },
    handleModelInputChange() {
      const matchedModel = this.availableModels.find(item => item.id === this.modelForm.model);
      this.selectedFetchedModel = matchedModel ? matchedModel.id : '';

      if (this.availableModels.length) {
        this.modelDropdownOpen = true;
        this.highlightedModelIndex = this.filteredAvailableModels.length ? 0 : -1;
      }
    },
    selectFetchedModel(item) {
      if (!item) return;
      this.selectedFetchedModel = item.id;
      this.modelForm.model = item.id;
      this.closeModelDropdown();
    },
    moveModelDropdown(step) {
      if (!this.availableModels.length) return;
      if (!this.modelDropdownOpen) {
        this.openModelDropdown();
        return;
      }

      const list = this.filteredAvailableModels;
      if (!list.length) {
        this.highlightedModelIndex = -1;
        return;
      }

      const lastIndex = list.length - 1;
      if (this.highlightedModelIndex < 0) {
        this.highlightedModelIndex = step > 0 ? 0 : lastIndex;
        return;
      }

      const nextIndex = this.highlightedModelIndex + step;
      if (nextIndex < 0) {
        this.highlightedModelIndex = lastIndex;
      } else if (nextIndex > lastIndex) {
        this.highlightedModelIndex = 0;
      } else {
        this.highlightedModelIndex = nextIndex;
      }
    },
    confirmHighlightedModel() {
      if (!this.modelDropdownOpen) return;
      const item = this.filteredAvailableModels[this.highlightedModelIndex];
      if (item) {
        this.selectFetchedModel(item);
      } else {
        this.closeModelDropdown();
      }
    },
    handleDocumentClick(event) {
      const root = this.$refs.modelCombobox;
      if (!root) return;
      if (root.contains(event.target)) return;
      this.closeModelDropdown();
    },
    nextStep() {
      this.step = 2;
    },
    handleProviderChange() {
      this.resetFetchedModelOptions();
      const provider = this.providers.find(p => p.id === this.modelForm.provider);
      if (provider && provider.configs && provider.configs.text) {
        this.modelForm.endpoint = provider.configs.text.endpoint;
      }
      // 切换服务商后不再默认填充模型，需手动输入或点击获取
      this.modelForm.model = '';
    },
    async fetchModelList() {
      if (this.modelListLoading) return;

      const validation = validateModelCatalogEndpoint(this.modelForm.endpoint);
      if (!validation.ok) {
        this.modelListError = this.getModelListValidationMessage(validation.code);
        this.modelListHint = '';
        return;
      }

      if (!String(this.modelForm.apiKey || '').trim()) {
        this.modelListError = this.$t('settingsModal.fetchModelsNeedApiKey');
        this.modelListHint = '';
        return;
      }

      const endpoint = validation.endpoint;
      this.modelListLoading = true;
      this.modelListError = '';
      this.modelListHint = '';
      this.availableModels = [];
      this.selectedFetchedModel = '';
      this.lastModelCatalogSource = '';

      try {
        const { models, sourceHost } = await requestModelCatalog({
          provider: this.modelForm.provider || '',
          type: this.modelForm.type || 'text',
          endpoint,
          apiKey: this.modelForm.apiKey || ''
        });

        this.availableModels = models;
        this.lastModelCatalogSource = sourceHost;

        if (!models.length) {
          this.modelListHint = this.$t('settingsModal.modelListEmpty');
          this.closeModelDropdown();
          return;
        }

        this.modelListHint = this.$t('settingsModal.fetchedModelsHint', { count: models.length });
        const matchedModel = models.find(item => item.id === this.modelForm.model);
        if (matchedModel) {
          this.selectedFetchedModel = matchedModel.id;
        } else if (models.length === 1 && !this.modelForm.model) {
          this.selectedFetchedModel = models[0].id;
          this.modelForm.model = models[0].id;
        }
        this.openModelDropdown();
      } catch (e) {
        this.modelListError = this.$t('settingsModal.fetchModelsFailedWithMessage', {
          message: e && e.message ? e.message : this.$t('settingsModal.fetchModelsFailed')
        });
      } finally {
        this.modelListLoading = false;
      }
    },
    saveAndStart() {
      if (!this.isValid) return;

      const newModel = {
        id: Date.now().toString(),
        ...this.modelForm
      };

      // Add the model to the store
      mutations.addModel(newModel);
      
      // Set as active text model
      mutations.setActiveModel(newModel.id, 'text');

      // Emit event to close wizard (parent should handle this based on store state change)
      this.$emit('finished');
    }
  }
};
</script>

<style scoped>
.wizard-overlay {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: var(--bg-main);
  display: block;
  z-index: 1000002;
  overflow: hidden;
}

.wizard-bg-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, var(--accent-color) 0%, transparent 70%);
  opacity: 0.15;
  pointer-events: none;
}

.wizard-container {
  background-color: var(--bg-surface);
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
}

.wizard-step {
  display: flex;
  flex-direction: column;
  height: 100%;
  animation: fadeIn 0.4s ease;
  position: relative;
}

.step-content-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  padding-bottom: 100px; /* Ensure content is not hidden behind fixed footer */
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.step-footer-fixed {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 20px;
  background: var(--bg-surface);
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: center;
  z-index: 20;
}

.step-actions.step-footer-fixed {
  justify-content: space-between;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Intro Header */
.intro-header {
  text-align: center;
  margin-bottom: 16px;
  position: relative;
}

.logo-container {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  height: 120%;
  background: radial-gradient(circle, var(--accent-color) 0%, transparent 70%);
  opacity: 0.4;
  filter: blur(10px);
}

.logo-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px solid var(--accent-color);
  opacity: 0.2;
  animation: ringPulse 3s infinite;
}

@keyframes ringPulse {
  0% { transform: scale(0.9); opacity: 0.3; }
  50% { transform: scale(1.1); opacity: 0.1; }
  100% { transform: scale(0.9); opacity: 0.3; }
}

.logo-icon {
  position: relative;
  z-index: 2;
  color: var(--accent-color);
  filter: drop-shadow(0 0 8px rgba(0,0,0, 0.2));
}

.hero-title {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 12px;
  letter-spacing: -0.5px;
  color: var(--text-primary);
}

.gradient-text {
  background: linear-gradient(135deg, var(--accent-color) 0%, #a855f7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  font-weight: 500;
  opacity: 0.8;
  margin: 0;
}

/* Info Card */
.info-card {
  margin-bottom: 32px;
  text-align: center;
}

.info-text {
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
}

.highlight {
  color: var(--accent-color);
  font-weight: 600;
}

/* Feature Grid */
.feature-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;
  margin-bottom: 32px;
}

.feature-card {
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 10px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.feature-card:hover {
  transform: translateY(-4px);
  border-color: var(--accent-color);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
}

.card-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.memory-bg {
  background: rgba(255, 99, 132, 0.1);
  color: #ff6384;
}

.privacy-bg {
  background: rgba(54, 162, 235, 0.1);
  color: #36a2eb;
}

.card-content h4 {
  margin: 0 0 8px 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}

.card-content p {
  margin: 0;
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.4;
}

/* Capability Badges */
.capability-badges {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 40px;
  flex-wrap: wrap;
}

.badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: var(--bg-input);
  border-radius: 20px;
  font-size: 12px;
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  transition: all 0.2s;
}

.badge:hover {
  background: var(--bg-surface);
  border-color: var(--accent-color);
  color: var(--accent-color);
}

/* Buttons */
.start-btn {
  background: linear-gradient(135deg, var(--accent-color) 0%, #7c3aed 100%);
  color: white;
  border: none;
  padding: 16px 48px;
  border-radius: 30px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(124, 58, 237, 0.3);
  position: relative;
  overflow: hidden;
}

.start-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(124, 58, 237, 0.4);
}

.start-btn::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.2), transparent);
  transform: translateX(-100%);
  transition: transform 0.5s;
}

.start-btn:hover::after {
  transform: translateX(100%);
}

.cool-btn {
  background: linear-gradient(135deg, var(--accent-color) 0%, #7c3aed 100%);
  color: white;
  border: none;
  padding: 12px 32px;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(124, 58, 237, 0.3);
}

.cool-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(124, 58, 237, 0.4);
}

.cool-btn.large-btn {
  padding: 14px 48px;
  font-size: 18px;
}

.cool-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Step 2 Styles */
.github-link-badge {
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s ease;
  z-index: 100;
}

.github-link-badge:hover {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
}

.github-link-badge svg {
  opacity: 0.9;
}

.github-repo-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 6px 12px;
  background: rgba(124, 58, 237, 0.1);
  color: var(--accent-color);
  border-radius: 6px;
  text-decoration: none;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s;
}

.github-repo-link:hover {
  background: var(--accent-color);
  color: white;
}

.step-header {
  text-align: center;
  margin-bottom: 30px;
}

.step-header h3 {
  font-size: 24px;
  margin-bottom: 8px;
  color: var(--text-primary);
}

.step-header p {
  color: var(--text-secondary);
  margin: 0;
}

.config-form {
  width: 100%;
  padding-right: 4px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--text-primary);
  font-size: 14px;
}

.provider-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.provider-label-row label {
  margin-bottom: 0;
}

.field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.field-label-row label {
  margin-bottom: 0;
}

.tutorial-link {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--accent-color);
  text-decoration: none;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 12px;
  background-color: rgba(124, 58, 237, 0.1);
  transition: all 0.2s;
}

.tutorial-link:hover {
  background-color: var(--accent-color);
  color: white;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--accent-color);
}

.inline-fetch-action {
  color: var(--accent-color);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s ease, color 0.2s ease;
}

.inline-fetch-action:hover {
  opacity: 0.8;
}

.inline-fetch-action.disabled {
  opacity: 0.55;
  cursor: not-allowed;
  pointer-events: none;
}

.custom-model-combobox {
  position: relative;
}

.custom-combobox-input {
  position: relative;
}

.custom-combobox-input.open .custom-combobox-field {
  background: var(--bg-surface);
  box-shadow: inset 0 0 0 1px rgba(124, 58, 237, 0.18);
}

.custom-combobox-input.disabled .custom-combobox-field {
  opacity: 0.78;
}

.custom-combobox-field {
  padding-right: 42px !important;
}

.custom-combobox-toggle {
  position: absolute;
  top: 50%;
  right: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  cursor: pointer;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

.custom-combobox-input.open .custom-combobox-toggle {
  color: var(--accent-color);
  transform: translateY(-50%) rotate(180deg);
}

.custom-combobox-toggle.disabled {
  opacity: 0.4;
  cursor: default;
}

.custom-combobox-panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 240px;
  overflow-y: auto;
  padding: 6px;
  border-radius: 14px;
  background: var(--bg-surface);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
  border: 1px solid var(--border-color);
}

.custom-combobox-option {
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease;
}

.custom-combobox-option:hover,
.custom-combobox-option.highlighted {
  background: rgba(124, 58, 237, 0.08);
}

.custom-combobox-option.active {
  background: rgba(124, 58, 237, 0.12);
}

.custom-combobox-option-title {
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
}

.custom-combobox-option-meta {
  margin-top: 2px;
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.4;
}

.custom-combobox-empty {
  padding: 12px;
  color: var(--text-secondary);
  font-size: 12px;
  text-align: center;
}

.model-fetch-feedback {
  margin-top: 4px;
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.45;
}

.model-fetch-feedback.error {
  color: #ef4444;
}

.form-group .hint {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-secondary);
}

.step-actions {
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
}

.btn-secondary {
  background-color: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  padding: 10px 24px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
}

.btn-secondary:hover {
  background-color: var(--bg-input);
}
</style>
